/** SPEC-OPP-021 — o arquivo de oportunidades fechadas.
 *
 * Os tipos descrevem **o que a API devolve**, conferido contra a resposta real
 * e não contra o exemplo da spec. Foi o engano da sessão 20: construir contra
 * o exemplo deu uma tela que quebrou na primeira chamada.
 */

import { http } from "@/lib/http";
import type { components } from "@/lib/api-types";

export type StatusDoArquivo = "all" | "won" | "lost";

/** O contrato tipa `status` como `string` (o Pydantic da rota não usa um
 * `Literal`/enum) — o front assume, e sempre assumiu, que o arquivo só
 * mostra oportunidades ganhas ou perdidas. Divergência real; ver relatório. */
export type ItemDoArquivo = Omit<components["schemas"]["ItemDoArquivoResponse"], "status"> & {
  status: "won" | "lost";
};

export interface PaginaDoArquivo {
  items: ItemDoArquivo[];
  meta: components["schemas"]["MetaDoArquivoResponse"];
}

export interface FiltrosDoArquivo {
  status?: StatusDoArquivo;
  desde?: string;
  ate?: string;
  companyId?: string;
  ownerId?: string;
  lostReason?: string;
  busca?: string;
  cursor?: string;
}

export async function buscarArquivo(filtros: FiltrosDoArquivo = {}): Promise<PaginaDoArquivo> {
  const parametros = new URLSearchParams();

  // `all` é o padrão do servidor; mandá-lo explicitamente só polui a URL que a
  // pessoa vê e eventualmente compartilha.
  if (filtros.status !== undefined && filtros.status !== "all") {
    parametros.set("status", filtros.status);
  }
  if (filtros.desde !== undefined && filtros.desde !== "") parametros.set("desde", filtros.desde);
  if (filtros.ate !== undefined && filtros.ate !== "") parametros.set("ate", filtros.ate);
  if (filtros.companyId !== undefined) parametros.set("company_id", filtros.companyId);
  if (filtros.ownerId !== undefined) parametros.set("owner_id", filtros.ownerId);
  if (filtros.lostReason !== undefined && filtros.lostReason !== "") {
    parametros.set("lost_reason", filtros.lostReason);
  }
  if (filtros.busca !== undefined && filtros.busca.trim() !== "") {
    parametros.set("q", filtros.busca.trim());
  }
  if (filtros.cursor !== undefined) parametros.set("cursor", filtros.cursor);

  const consulta = parametros.toString();
  return http.get<PaginaDoArquivo>(
    `/opportunities/archive${consulta === "" ? "" : `?${consulta}`}`,
  );
}

/** SPEC-OPP-014 — devolve a oportunidade ao funil.
 *
 * A rota existe desde a implementação do board e **nunca teve de onde ser
 * chamada**: o arquivo é a primeira tela que alcança uma oportunidade fechada,
 * e portanto a primeira em que reabrir faz sentido.
 */
export async function reabrir(id: string, expectedVersion: number): Promise<void> {
  // `expected_version`, e não `version`: o nome do campo é o do contrato, e
  // errá-lo devolveria 422 só em produção — o Pydantic ignora campo extra e
  // reclama do obrigatório ausente.
  await http.post(`/opportunities/${id}/reopen`, { expected_version: expectedVersion });
}
